import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { useJobEvents } from '@/api/jobs';
import type { Website } from '@/api/types';
import { paths } from '@/app/paths';
import { Button, Card, Icon, LandingMini, Pill, useToast, type Tone } from '@/design-system';
import { formatUsd } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { applyVariantEvent, landingPalette, retryVariant, RUNNING, screenshot, variantsKey, variantsQuery, type VariantOut } from './api';
import s from './variants.module.css';

const TONE: Record<'running' | 'ready' | 'failed', Tone> = { running: 'acc', ready: 'ok', failed: 'err' };

/** «2:01» from milliseconds. */
export function duration(ms: number | null): string | null {
  if (ms === null) return null;
  const sec = Math.round(ms / 1000);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/** The active variant's key from the website's label («B · r14» → B). */
export const activeKey = (site: Website) => site.variant?.slice(0, 1) ?? null;

/**
 * /sites/:id/variants — «Варианты» (DESIGN_SPEC §4.5): one card per variant of the latest round: the
 * mini landing preview in the variant's palette (its screenshot once the build has one), the letter,
 * the brief and the status pill with live progress, the cost, tokens and time, and «Открыть в AI
 * Designer» — or, when it failed, the mono error and «Повторить генерацию». The list polls while a
 * variant is running and refreshes on its jobs' events.
 */
export function VariantsTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('variants');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery(variantsQuery(site.id));
  // The event stream says when a variant job moves (between polls, and after the last one).
  useJobEvents((e) => {
    // `job.variant` carries a variant's state (P4-B04): applied to the cached round, or a refetch when the
    // round is not cached yet (a new generation).
    if (e.type === 'job.variant' && !applyVariantEvent(qc, site.id, e)) void qc.invalidateQueries({ queryKey: variantsKey(site.id) });
  });
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  if (!q.data.length)
    return (
      <Card className={s.empty}>
        <Icon name="copy" size={24} className={s.emptyIcon} />
        <h2 className={s.emptyTitle}>{t('empty_title')}</h2>
        <span className={s.emptyText}>{t('empty_text')}</span>
        <div>
          <Button variant="secondary" size={36} icon="layers" iconSize={14} onClick={() => navigate(paths.site(site.id, 'structure'))}>
            {t('to_structure')}
          </Button>
        </div>
      </Card>
    );
  return (
    <div className={s.grid} role="list" aria-label={t('label')}>
      {q.data.map((v) => (
        <VariantCard key={v.id} site={site} v={v} current={activeKey(site) === v.key} />
      ))}
    </div>
  );
}

function VariantCard({ site, v, current }: { site: Website; v: VariantOut; current: boolean }) {
  const { t } = useTranslation('variants');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const running = RUNNING.has(v.status);
  const state = running ? 'running' : v.status === 'failed' ? 'failed' : 'ready';
  const pct = Math.round(Math.max(0, Math.min(1, v.progress)) * 100);
  const pill = running ? t('status.running', { step: t(`status.${v.status}`), pct }) : t(`status.${state}`);
  const tokens = (v.tokens.input ?? 0) + (v.tokens.output ?? 0);
  const cost = Number(v.cost_usd);
  const dash = tc('dash');
  const retry = () =>
    retryVariant(v.id)
      .then(() => {
        void qc.invalidateQueries({ queryKey: variantsKey(site.id) });
        toast(t('retried', { key: v.key }));
      })
      .catch((e: unknown) => toast(errorText(e, tc)));
  return (
    <article role="listitem" className={s.card} aria-current={current} aria-label={`${v.key} · ${v.brief.title || t(`briefs.${v.key}.title`)}`}>
      <LandingMini palette={landingPalette(v)} brand={site.organization.brand_name.split(' ')[0] ?? ''} headline={t('headline')} screenshotUrl={state === 'ready' ? screenshot(v, '834') : null}>
        {running && (
          <div className={s.running}>
            <div className={s.track} role="progressbar" aria-label={t('progress', { key: v.key })} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className={s.fill} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {state === 'failed' && (
          <div className={s.failed}>
            <span>{t('fail', { code: v.error_code ?? 'build' })}</span>
            {v.error_detail && <span className={s.failDetail}>{v.error_detail}</span>}
          </div>
        )}
      </LandingMini>
      <div className={s.body}>
        <div className={s.head}>
          <div className={s.who}>
            <span className={s.letter}>{v.key}</span>
            <h3 className={s.brief}>{v.brief.title || t(`briefs.${v.key}.title`)}</h3>
          </div>
          <Pill tone={TONE[state]}>{pill}</Pill>
        </div>
        {/* The brief's `direction` is the model's (English) instruction: the card's line is the key's fixed summary. */}
        <span className={s.summary}>{t(`briefs.${v.key}.summary`)}</span>
        <div className={s.stats}>
          <div className={s.stat}>
            <span className={s.statLabel}>{t('stats.cost')}</span>
            <span className={s.statValue}>{cost > 0 ? formatUsd(cost, 2) : dash}</span>
          </div>
          <div className={s.stat}>
            <span className={s.statLabel}>{t('stats.tokens')}</span>
            <span className={s.statValue}>{tokens > 0 ? t('stats.tokens_value', { value: Math.round(tokens / 1000) }) : dash}</span>
          </div>
          <div className={s.stat}>
            <span className={s.statLabel}>{t('stats.time')}</span>
            <span className={s.statValue}>{v.duration_ms > 0 ? duration(v.duration_ms) : dash}</span>
          </div>
        </div>
        {state === 'ready' && (
          <Button variant="light" spread trailingIcon="arrow-right" iconSize={15} onClick={() => navigate(`${paths.site(site.id, 'designer')}?variant=${v.key}`)}>
            {t('open')}
          </Button>
        )}
        {state === 'failed' && (
          <Button variant="danger" spread trailingIcon="rotate-ccw" iconSize={15} onClick={() => void retry()}>
            {t('retry')}
          </Button>
        )}
      </div>
    </article>
  );
}
